import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  listMyApiTokens,
  createMyApiToken,
  revokeMyApiToken,
} from '../../services/api';
import { formatDateTime } from '../../utils/formatDateTime';

/**
 * TenantApiTokens — UI de gerenciamento de API tokens do tenant.
 *
 * Tenants com a feature `api_access` (Enterprise) podem gerar Bearer
 * tokens pra consumir a API pública /v1/*. Esta página lista tokens
 * ativos e permite criar/revogar.
 *
 * Fluxo de criação: ao clicar "Novo token", abre modal pedindo nome +
 * escopo. Submit chama a API e mostra o plaintext UMA vez num card
 * destacado com botão "Copiar" — depois dessa tela, o plaintext some
 * para sempre e o usuário só vê o prefix na listagem.
 */
export default function TenantApiTokens() {
  const confirm = useConfirm();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', scope: 'read' });
  const [creating, setCreating] = useState(false);
  const [newlyCreated, setNewlyCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setLoading(true);
    listMyApiTokens()
      .then(r => setTokens(r.data.tokens || []))
      .catch(err => {
        const msg = err.response?.data?.error || 'Erro ao carregar tokens';
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ name: '', scope: 'read' });
    setNewlyCreated(null);
    setCopied(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setNewlyCreated(null);
    setCopied(false);
  };

  const handleCreate = async e => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error('Nome do token precisa ter pelo menos 2 caracteres');
      return;
    }
    setCreating(true);
    try {
      const res = await createMyApiToken({
        name: form.name.trim(),
        scope: form.scope,
      });
      setNewlyCreated(res.data.token);
      load();
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao criar token';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!newlyCreated?.token) return;
    try {
      await navigator.clipboard.writeText(newlyCreated.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Não foi possível copiar automaticamente — selecione o texto e copie manualmente');
    }
  };

  const handleRevoke = async t => {
    const ok = await confirm({
      title: `Revogar token "${t.name}"?`,
      message: `Após revogar, qualquer integração que usa este token vai parar de funcionar imediatamente.\n\nPrefix: ${t.prefix}\n\nEsta ação não pode ser desfeita.`,
      confirmText: 'Revogar token',
      cancelText: 'Voltar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await revokeMyApiToken(t.id);
      toast.success('Token revogado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao revogar');
    }
  };

  const fmtDate = formatDateTime;

  const scopeLabel = s => s === 'write' ? 'Leitura + Escrita' : 'Somente leitura';
  const scopeBadge = s => s === 'write' ? 'badge-warning' : 'badge-info';

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>🔑 API Tokens</h1>
          <p className="page-subtitle">Gerencie tokens de acesso para integrar sistemas externos com o AtendoApp via API REST.</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Novo token</button>
      </div>

      <div className="info-box" style={{ marginBottom: 20, fontSize: 13 }}>
        📖 <strong>Como usar:</strong> depois de criar, use o token no header <code>Authorization: Bearer atk_live_...</code> nas chamadas a <code>GET /api/v1/appointments</code>, <code>/v1/clients</code> e <code>/v1/services</code>. Consulte a documentação completa da API com seu time de suporte.
      </div>

      {loading ? (
        <div className="spinner" style={{ margin: '40px auto', display: 'block' }} />
      ) : tokens.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔑</div>
          <h3>Nenhum token criado ainda</h3>
          <p>Clique em "Novo token" para gerar seu primeiro Bearer token.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="card-body" style={{ padding: 0 }}>
            {tokens.map(t => (
              <div key={t.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 18px', borderBottom: '1px solid var(--gray-100)',
                gap: 12, flexWrap: 'wrap',
                opacity: t.active ? 1 : 0.55,
              }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {t.name}
                    <span className={`badge ${scopeBadge(t.scope)}`} style={{ fontSize: 10 }}>{scopeLabel(t.scope)}</span>
                    {!t.active && <span className="badge badge-gray" style={{ fontSize: 10 }}>Revogado</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3, fontFamily: 'monospace' }}>
                    atk_live_{t.prefix}•••••••
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>
                    Criado {fmtDate(t.created_at)}
                    {t.last_used_at && ` · Último uso ${fmtDate(t.last_used_at)}`}
                    {t.revoked_at && ` · Revogado ${fmtDate(t.revoked_at)}`}
                  </div>
                </div>
                {t.active && (
                  <button className="btn btn-danger btn-sm" onClick={() => handleRevoke(t)}>
                    Revogar
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3>{newlyCreated ? '✅ Token criado' : 'Novo API token'}</h3>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            {!newlyCreated ? (
              <form onSubmit={handleCreate}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Nome *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Ex: Integração CRM interno"
                      autoFocus
                      maxLength={100}
                    />
                    <small>Escolha um nome descritivo pra identificar depois onde está sendo usado.</small>
                  </div>
                  <div className="form-group">
                    <label>Escopo *</label>
                    <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                      <label style={{ flex: 1, padding: 12, border: '1.5px solid var(--gray-200)', borderRadius: 8, cursor: 'pointer', background: form.scope === 'read' ? 'var(--gray-50)' : 'transparent', borderColor: form.scope === 'read' ? 'var(--primary)' : 'var(--gray-200)' }}>
                        <input type="radio" name="scope" value="read" checked={form.scope === 'read'} onChange={() => setForm(f => ({ ...f, scope: 'read' }))} style={{ marginRight: 6 }} />
                        <strong>Somente leitura</strong>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>GET em appointments, clients, services</div>
                      </label>
                      <label style={{ flex: 1, padding: 12, border: '1.5px solid var(--gray-200)', borderRadius: 8, cursor: 'pointer', background: form.scope === 'write' ? 'var(--gray-50)' : 'transparent', borderColor: form.scope === 'write' ? 'var(--primary)' : 'var(--gray-200)' }}>
                        <input type="radio" name="scope" value="write" checked={form.scope === 'write'} onChange={() => setForm(f => ({ ...f, scope: 'write' }))} style={{ marginRight: 6 }} />
                        <strong>Leitura + escrita</strong>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>Também permite criar/atualizar agendamentos</div>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-ghost" onClick={closeModal}>Cancelar</button>
                  <button type="submit" className={`btn btn-primary${creating ? ' btn-loading' : ''}`} disabled={creating}>
                    {creating ? 'Criando…' : 'Criar token'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="modal-body">
                  <div className="highlight-box" style={{ marginBottom: 16, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                    ⚠️ <strong>Copie o token agora!</strong> Este é o único momento em que ele será exibido. Depois que você fechar esta tela, não haverá como recuperá-lo — se perder, precisará criar um novo.
                  </div>
                  <div className="form-group">
                    <label>Token</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="text"
                        value={newlyCreated.token}
                        readOnly
                        style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--gray-50)', flex: 1 }}
                        onFocus={e => e.target.select()}
                      />
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleCopy}>
                        {copied ? '✓ Copiado' : 'Copiar'}
                      </button>
                    </div>
                  </div>
                  <div className="form-group" style={{ fontSize: 12, color: 'var(--gray-600)' }}>
                    <strong>Use assim:</strong>
                    <pre style={{ background: 'var(--gray-50)', padding: 10, borderRadius: 6, fontSize: 11, marginTop: 6, overflow: 'auto' }}>
{`curl https://atendoapp.com/api/v1/appointments \\
  -H "Authorization: Bearer ${newlyCreated.token}"`}
                    </pre>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary" onClick={closeModal}>
                    Entendi, fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
